import { createClient } from "microcms-js-sdk";

/**
 * Class NewsArticle
 */
class NewsArticle {
	transition: number;
	contentId: string;
	loadingElement: JQuery;
	block: JQuery;
	client: ReturnType<typeof createClient>;

	constructor(element: JQuery) {
		this.block = element;
		this.transition = 400;
		this.contentId = "";
		this.loadingElement = $("#js_loading");
		// microCMSクライアントの初期化
		this.client = createClient({
			serviceDomain: "3kxo8u9nf1", // microCMSのサービスドメイン
			apiKey: "c6086e40236a405c9410b8c52bed50b428f0", //画面プレビュー用
			// apiKey: 'ac14fd9441e74441bf3d27c001e7ddb131fb', //本番用
		});

		this.init();
	}

	/**
	 * Initialize
	 */
	init() {
		this.initializeArticle();
	}

	/**
	 * 初期化処理
	 * 初回ロード時にクエリパラメータからcontentIdを取得
	 */
	initializeArticle() {
		const urlParams = new URLSearchParams(window.location.search);
		this.contentId = urlParams.get("contentId") || "";
		if (!this.contentId) {
			console.error("contentIdが見つかりません。");
		} else {
			this.fetchArticle();
		}
	}

	/**
	 * Fetch article from the API
	 */
	async fetchArticle() {
		// loadingを表示
		this.loadingElement.addClass("is_show");

		// APIリクエスト
		try {
			const data = await this.client.get({
				endpoint: "toray_news",
				contentId: this.contentId,
			});
			this.updateJsonLd(data);
			this.updateArticleDetails(data);
		} catch (error) {
			console.error("Error fetching article:", error);
		} finally {
			// loading非表示
			setTimeout(() => {
				this.block.addClass("is_show");
				this.loadingElement.removeClass("is_show");
			}, this.transition);
		}
	}

	/**
	 * Update jsonld with API data
	 * @param data - APIレスポンスデータ
	 */
	updateJsonLd(data: any) {
		// JSON-LDスクリプトタグを取得
		const jsonLdElement = document.querySelector("script[type='application/ld+json']");
		if (!jsonLdElement) {
			console.error("JSON-LDスクリプトが見つかりません");
			return;
		}

		// 現在のJSON-LDをパース
		let jsonLdData;
		try {
			jsonLdData = JSON.parse(jsonLdElement.textContent || "{}");
		} catch (error) {
			console.error("JSON-LDのパースに失敗しました:", error);
			return;
		}

		// 現在のURLを取得
		const currentUrl = window.location.href;

		// JSON-LDデータの変更
		const contentId = data.id || "unknown-id";

		// 動的データを反映
		jsonLdData["@graph"][0]["@id"] = currentUrl;
		jsonLdData["@graph"][0].url = currentUrl;
		jsonLdData["@graph"][0].name = `${data.title} | Media &amp; News | TORAY United States`;
		jsonLdData["@graph"][0].breadcrumb["@id"] = `${currentUrl}#breadcrumb`;
		jsonLdData["@graph"][0].potentialAction[0].target = [currentUrl];
		jsonLdData["@graph"][1]["@id"] = `${currentUrl}#breadcrumb`;
		jsonLdData["@graph"][1].itemListElement[2].name = data.title;

		// JSON-LDデータをスクリプトに反映
		jsonLdElement.textContent = JSON.stringify(jsonLdData, null, 2);
	}

	/**
	 * Update article detail with API data
	 * @param data - APIレスポンスデータ
	 */
	updateArticleDetails(data: any) {
		// metaデータの更新（既存のコードをそのまま保持）
		document.title = `${data.title} | Media &amp; News | TORAY United States`;
		document
			.getElementById("js_og_title")
			?.setAttribute("content", `${data.title} | Media &amp; News | TORAY United States`);
		document.getElementById("js_og_url")?.setAttribute("content", window.location.href);
		document.getElementById("js_canonical")?.setAttribute("href", window.location.href);

		// タイトルを更新
		document.querySelector(".block-blog-header__title h1")!.innerHTML = data.title;

		// 日付を更新
		const dateElement = document.querySelector(".block-blog-header__date time");
		if (dateElement) {
			const date = new Date(data.date);
			dateElement.textContent = date.toLocaleDateString("en-US", {
				month: "2-digit",
				day: "2-digit",
				year: "2-digit",
			});
		}

		// カテゴリーを更新
		const categoryElement = document.querySelector(".block-blog-header__category span");
		if (categoryElement) {
			if (data.newsCategory && data.newsCategory.name_en) {
				categoryElement.textContent = data.newsCategory.name_en;
			} else {
				categoryElement.textContent = "News";
			}
		}

		// 著者を更新
		const authorElement = document.querySelector(".block-blog-header__author");
		if (authorElement) {
			authorElement.textContent = data.author || "";
		}

		// 本文を更新
		const contentElement = document.getElementById("js_news_article");
		if (contentElement && data.article && data.article.length) {
			let post = "";
			for (let i = 0; i < data.article.length; i++) {
				const item = data.article[i];
				switch (item.fieldId) {
					case "editor":
						post += item.html;
						break;
					case "imageText":
						{
							const src = item.image.url;
							const alt = item.image.alt || "";
							const text = item.text || "";
							const caption = item.caption || "";
							const position = item.position;
							const wrap = position
								? '<div class="bl_media">'
								: '<div class="bl_media -reverse">';
							post += `
                            ${wrap}
                                <figure class="mediaImg">
                                    <img loading="lazy" src="${src}" alt="${alt}">
																		<figcaption>${caption}</figcaption>
                                </figure>
                                <div class="mediaBody">${text}</div>
                            </div>`;
						}
						break;
					case "imageCol1":
						{
							const src = item.media.url;
							const alt = item.caption || "";
							const caption = item.caption || "";
							post += `
                            <figure class="bl_imgWrap -slim">
                                <img loading="lazy" src="${src}" alt="${alt}">
                                <figcaption>${caption}</figcaption>
                            </figure>`;
						}
						break;
					case "imageCol2":
						{
							const src1 = item.image1.url;
							const src2 = item.image2.url;
							const alt1 = item.caption1 || "";
							const alt2 = item.caption2 || "";
							const caption1 = item.caption1 || "";
							const caption2 = item.caption2 || "";
							post += `
                            <div class="bl_cardUnit">
                                <div class="bl_card">
                                    <figure class="cardImg">
                                        <img loading="lazy" src="${src1}" alt="${alt1}">
                                        <figcaption>${caption1}</figcaption>
                                    </figure>
                                </div>
                                <div class="bl_card">
                                    <figure class="cardImg">
                                        <img loading="lazy" src="${src2}" alt="${alt2}">
                                        <figcaption>${caption2}</figcaption>
                                    </figure>
                                </div>
                            </div>`;
						}
						break;
					case "pdf":
						{
							const pdfpath = item.file.url;
							const text = item.text;
							post += `<p><a href="${pdfpath}" class="_labelPDF" target="_blank">${text}</a></p>`;
						}
						break;
					case "linkButton":
						{
							const path = item.path;
							const text = item.text;
							const target = item.target;
							const blankclass = target ? " -blank" : "";
							const targetAttr = target
								? ' target="_blank" rel="noopener noreferrer nofollow"'
								: "";
							post += `<div class="bl_btnWrap"><a href="${path}" class="el_btn -stand${blankclass}"${targetAttr}>${text}</a></div>`;
						}
						break;
					default:
						break;
				}
			}
			contentElement.innerHTML = post;
		}

		// インポートデータ（既存のコードをそのまま保持）
		if (data.importArticle) {
			const importDataElement = document.getElementById("js_news_import");
			if (importDataElement) {
				importDataElement.innerHTML = data.importArticle;
			}
		}
	}
}

// クラスインスタンスの作成
const newsArticle: NewsArticle[] = [];
$(".block-blog-content").each(function () {
	const newsArticleInstance = new NewsArticle($(this));
	newsArticle.push(newsArticleInstance);
});
